/**
 * Dark mode Connect AI palette — deeper space dark grey (2 tones darker).
 */
const colors = {
  background: {
    default: "#08090C",
    paper: "#0E1014",
    card: "#12141A",
    sidenav: "#0A0B0F",
    cardBorder: "#1C1F28",
    cardBorderLeft: "#83BAB7",
    section: "#0E1014",
    sectionAlt: "#12141A",
    tonal: "rgba(148, 156, 172, 0.07)",
    tonalHover: "rgba(148, 156, 172, 0.12)",
  },

  text: {
    primary: "#D8DBE2",
    secondary: "#8B919D",
    disabled: "rgba(216, 219, 226, 0.36)",
    main: "#D8DBE2",
    focus: "#E6E8ED",
  },

  divider: "#1C1F28",

  action: {
    active: "rgba(216, 219, 226, 0.52)",
    hover: "rgba(148, 156, 172, 0.08)",
    selected: "rgba(131, 186, 183, 0.12)",
    disabled: "rgba(216, 219, 226, 0.36)",
    disabledBackground: "rgba(216, 219, 226, 0.08)",
    focus: "rgba(131, 186, 183, 0.20)",
  },

  transparent: {
    main: "rgba(0, 0, 0, 0)",
  },

  white: {
    main: "#E6E8ED",
    focus: "#F2F3F6",
  },

  black: {
    light: "#1C1F28",
    main: "#050608",
    focus: "#020305",
  },

  primary: {
    main: "#83BAB7",
    light: "#A8D0CD",
    dark: "#4F8F8B",
    focus: "#A8D0CD",
    contrastText: "#08090C",
  },

  secondary: {
    main: "#8B919D",
    light: "#8DA060",
    dark: "#636976",
    focus: "#B0B5C0",
    contrastText: "#08090C",
  },

  info: {
    main: "#83BAB7",
    light: "#A8D0CD",
    dark: "#4F8F8B",
    focus: "#A8D0CD",
    contrastText: "#08090C",
  },

  success: {
    main: "#8DA060",
    light: "#A8B87E",
    dark: "#6B8248",
    focus: "#A8B87E",
    contrastText: "#08090C",
  },

  warning: {
    main: "#FBBF24",
    light: "#FCD34D",
    dark: "#F59E0B",
    focus: "#FCD34D",
    contrastText: "#0A0B0F",
  },

  error: {
    main: "#F87171",
    light: "#FCA5A5",
    dark: "#EF4444",
    focus: "#FCA5A5",
    contrastText: "#0A0B0F",
  },

  light: {
    main: "#12141A",
    focus: "#1C1F28",
  },

  dark: {
    main: "#050608",
    focus: "#020305",
  },

  grey: {
    50: "#0A0B0F",
    100: "#0E1014",
    200: "#12141A",
    300: "#1C1F28",
    400: "#2A2E38",
    500: "#636976",
    600: "#8B919D",
    700: "#B0B5C0",
    800: "#D8DBE2",
    900: "#E6E8ED",
  },

  priority: {
    critical: { main: "#F87171", focus: "#FCA5A5" },
    high: { main: "#FBBF24", focus: "#FCD34D" },
    medium: { main: "#38BDF8", focus: "#7DD3FC" },
    low: { main: "#34D399", focus: "#6EE7B7" },
  },

  status: {
    new: { main: "#38BDF8", focus: "#7DD3FC" },
    assigned: { main: "#FBBF24", focus: "#FCD34D" },
    inProgress: { main: "#A78BFA", focus: "#C4B5FD" },
    onHold: { main: "#8B919D", focus: "#B0B5C0" },
    resolved: { main: "#34D399", focus: "#6EE7B7" },
    closed: { main: "#7A808C", focus: "#B0B5C0" },
    cancelled: { main: "#F87171", focus: "#FCA5A5" },
  },

  gradients: {
    primary: { main: "#83BAB7", state: "#4F8F8B" },
    secondary: { main: "#8B919D", state: "#636976" },
    info: { main: "#83BAB7", state: "#4F8F8B" },
    success: { main: "#8DA060", state: "#6B8248" },
    warning: { main: "#FBBF24", state: "#F59E0B" },
    error: { main: "#F87171", state: "#EF4444" },
    light: { main: "#12141A", state: "#1C1F28" },
    dark: { main: "#08090C", state: "#050608" },
  },

  socialMediaColors: {
    facebook: { main: "#1877F2", dark: "#166FE5" },
    twitter: { main: "#1DA1F2", dark: "#1A91DA" },
    instagram: { main: "#E4405F", dark: "#D93A57" },
    linkedin: { main: "#0077B5", dark: "#005885" },
    pinterest: { main: "#BD081C", dark: "#8C0615" },
    youtube: { main: "#FF0000", dark: "#CC0000" },
    vimeo: { main: "#1AB7EA", dark: "#13A3D2" },
    slack: { main: "#4A154B", dark: "#350D36" },
    dribbble: { main: "#EA4C89", dark: "#E73177" },
    github: { main: "#24292E", dark: "#171A1D" },
    reddit: { main: "#FF4500", dark: "#E03D00" },
    tumblr: { main: "#35465C", dark: "#2A3749" },
  },

  badgeColors: {
    primary: { background: "rgba(131, 186, 183, 0.14)", text: "#A8D0CD" },
    secondary: { background: "rgba(139, 145, 157, 0.14)", text: "#B0B5C0" },
    info: { background: "rgba(131, 186, 183, 0.14)", text: "#A8D0CD" },
    success: { background: "rgba(141, 160, 96, 0.14)", text: "#A8B87E" },
    warning: { background: "rgba(251, 191, 36, 0.14)", text: "#FCD34D" },
    error: { background: "rgba(248, 113, 113, 0.14)", text: "#FCA5A5" },
    light: { background: "#12141A", text: "#8B919D" },
    dark: { background: "#1C1F28", text: "#D8DBE2" },
  },

  coloredShadows: {
    primary: "#83BAB7",
    secondary: "#8B919D",
    info: "#83BAB7",
    success: "#8DA060",
    warning: "#FBBF24",
    error: "#F87171",
    light: "#636976",
    dark: "#020305",
  },

  inputBorderColor: "#2A2E38",

  tabs: {
    indicator: { boxShadow: "#1C1F28" },
  },

  reportChat: {
    appBg: "#08090C",
    railBg: "#0A0B0F",
    railBorder: "#1C1F28",
    surface: "#0E1014",
    composerBg: "#0E1014",
    userBubble: "#16181F",
    userText: "#D8DBE2",
    assistantText: "#D8DBE2",
    muted: "#8B919D",
    border: "#1C1F28",
    selectedList: "rgba(131, 186, 183, 0.12)",
    accentGrad: "linear-gradient(135deg, #83BAB7 0%, #4F8F8B 100%)",
    sendIcon: "#08090C",
    thinking: "#8B919D",
    sparkle: "#83BAB7",
    errorText: "#F87171",
    listeningBg: "#F87171",
    listeningHoverBg: "#EF4444",
    overlayLight: "rgba(0, 0, 0, 0.04)",
    overlayDark: "rgba(255, 255, 255, 0.05)",
    surfaceOverlayLight: "rgba(0, 0, 0, 0.04)",
    surfaceOverlayDark: "rgba(255, 255, 255, 0.04)",
    elevatedSurfaceLight: "rgba(0, 0, 0, 0.02)",
    elevatedSurfaceDark: "rgba(255, 255, 255, 0.03)",
    chipSelectedLight: "rgba(61, 122, 118, 0.12)",
    chipSelectedDark: "rgba(131, 186, 183, 0.12)",
    composerSurfaceLight: "rgba(255, 255, 255, 0.96)",
    composerSurfaceDark: "rgba(14, 16, 20, 0.96)",
    shadowLight: "0 1px 4px rgba(0, 0, 0, 0.12)",
    shadowDark: "0 2px 12px rgba(0, 0, 0, 0.65)",
    shadowComposerLight: "0 2px 12px rgba(0, 0, 0, 0.12)",
    shadowComposerDark: "0 2px 12px rgba(0, 0, 0, 0.65)",
    insetBorderLight: "inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
    insetBorderDark: "inset 0 0 0 1px rgba(255, 255, 255, 0.05)",
    hoverMutedLight: "rgba(148, 163, 184, 0.12)",
    hoverMutedDark: "rgba(255, 255, 255, 0.05)",
    printBorder: "#1C1F28",
    printHeaderBg: "#12141A",
    printText: "#D8DBE2",
  },
};

export default colors;
